import { apiGet, apiPost, apiDelete } from './client';
import type {
  OperationResult,
  PagedItemsResult,
  SystemPromptDto,
} from './types';

// ai.SystemPrompt — reusable instruction blocks composed into agent prompts
// via SystemPromptAiCapability → AiCapability → AiAgentCapability.

export interface SystemPromptSearchParams {
  search?: string;
  globals?: boolean;
  pageNo?: number;
  pageSize?: number;
}

export async function searchSystemPrompts(
  params: SystemPromptSearchParams = {}
): Promise<PagedItemsResult<SystemPromptDto>> {
  const { search = '', globals, pageNo = 1, pageSize = 100 } = params;
  const wrapped = await apiGet<OperationResult<PagedItemsResult<SystemPromptDto>>>(
    '/api/SystemPrompt/Search',
    {
      search,
      pageNo,
      pageSize,
      ...(globals != null ? { globals } : {}),
    }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load system prompts');
  }
  return wrapped.data;
}

export async function getSystemPromptById(id: number): Promise<SystemPromptDto> {
  const wrapped = await apiGet<OperationResult<SystemPromptDto>>(`/api/SystemPrompt/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `System prompt ${id} not found`);
  }
  return wrapped.data;
}

export async function addSystemPrompt(
  model: Partial<SystemPromptDto>
): Promise<SystemPromptDto> {
  const wrapped = await apiPost<OperationResult<SystemPromptDto>>('/api/SystemPrompt/Add', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to create system prompt');
  }
  return wrapped.data;
}

export async function updateSystemPrompt(
  model: Partial<SystemPromptDto>
): Promise<SystemPromptDto> {
  const wrapped = await apiPost<OperationResult<SystemPromptDto>>('/api/SystemPrompt/Update', model);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to update system prompt');
  }
  return wrapped.data;
}

export async function removeSystemPrompt(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/SystemPrompt/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete system prompt');
}
